import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { UploadSimple } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DrawingGroup, groupByTitle, fmtSize } from "@/components/DrawingList.helpers";

const DRAWING_ACCEPT = "application/pdf,image/png,image/jpeg,application/octet-stream,.dwg,.dxf,application/dwg,image/vnd.dwg,image/x-dwg,application/acad,application/x-acad,application/autocad_dwg,application/dxf,image/vnd.dxf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

/**
 * DrawingList — versioned drawing/revision attachments component.
 * Iter47 refactor: view + row + group components extracted into
 * `DrawingList.helpers.jsx`, dropping cyclomatic complexity from 31 → 10.
 */
export function DrawingList({ parentType, parentId, category = "drawing", canEdit = true }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [openTitles, setOpenTitles] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ title: "", revision: "R0", revision_notes: "" });
  const [pendingFile, setPendingFile] = useState(null);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    if (!parentId) return;
    const { data } = await api.get("/attachments", { params: { parent_type: parentType, parent_id: parentId, category } });
    setFiles(data);
  }, [parentType, parentId, category]);
  useEffect(() => { load(); }, [load]);

  const pick = () => inputRef.current?.click();
  const onSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPendingFile(file);
    setForm({ title: file.name.replace(/\.[^.]+$/, ""), revision: "R0", revision_notes: "" });
    setDialogOpen(true);
  };

  const doUpload = async () => {
    if (!pendingFile) return;
    if (pendingFile.size > 20 * 1024 * 1024) return toast.error("File too large (max 20 MB)");
    if (!form.title.trim()) return toast.error("Give this drawing a title");
    const fd = new FormData();
    fd.append("file", pendingFile);
    setUploading(true);
    try {
      await api.post("/attachments/upload", fd, {
        params: {
          parent_type: parentType, parent_id: parentId, category,
          title: form.title, revision: form.revision, revision_notes: form.revision_notes,
        },
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded ${form.title} · ${form.revision}`);
      setDialogOpen(false); setPendingFile(null);
      if (inputRef.current) inputRef.current.value = "";
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setUploading(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this drawing?")) return;
    try { await api.delete(`/attachments/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const groups = groupByTitle(files);

  return (
    <div data-testid={`drawing-list-${parentType}-${parentId}`}>
      {canEdit && (
        <div className="mb-3">
          <input ref={inputRef} type="file" accept={DRAWING_ACCEPT} onChange={onSelect} className="hidden" data-testid="drawing-file-input" />
          <Button onClick={pick} disabled={uploading} className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] h-9 w-full" size="sm" data-testid="drawing-upload-btn">
            <UploadSimple size={14} className="mr-1" /> Upload drawing / revision
          </Button>
        </div>
      )}

      {Object.keys(groups).length === 0 ? (
        <div className="text-[12px] text-slate-400 py-4 text-center border border-dashed border-slate-200 rounded-sm">No drawings uploaded.</div>
      ) : (
        <div className="space-y-1">
          {Object.entries(groups).map(([title, revs]) => (
            <DrawingGroup
              key={title}
              title={title}
              revs={revs}
              isOpen={openTitles[title] ?? true}
              onToggle={() => setOpenTitles((s) => ({ ...s, [title]: !(s[title] ?? true) }))}
              canEdit={canEdit}
              onDelete={del}
            />
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) { setDialogOpen(false); setPendingFile(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">Upload drawing</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="label-caps mb-1.5">Drawing title</div>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ground Floor Duct Layout" data-testid="drawing-title-input" />
              <div className="text-[10px] text-slate-500 mt-1">Same title = grouped as revisions of the same drawing.</div>
            </div>
            <div>
              <div className="label-caps mb-1.5">Revision</div>
              <Input value={form.revision} onChange={(e) => setForm({ ...form, revision: e.target.value })} placeholder="R0 / R1 / R2…" data-testid="drawing-revision-input" />
            </div>
            <div>
              <div className="label-caps mb-1.5">Revision notes</div>
              <Textarea rows={2} value={form.revision_notes} onChange={(e) => setForm({ ...form, revision_notes: e.target.value })} placeholder="What changed since the previous revision?" />
            </div>
            <div className="text-[10.5px] text-slate-500 bg-slate-50 border border-slate-200 rounded-sm px-2.5 py-1.5">
              File: <span className="font-mono">{pendingFile?.name}</span> · {pendingFile ? fmtSize(pendingFile.size) : ""}
            </div>
            <Button onClick={doUpload} disabled={uploading} className="w-full rounded-sm bg-[#0F172A] hover:bg-slate-800" data-testid="drawing-save-btn">
              {uploading ? "Uploading…" : "Upload"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
